import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  PawPrint, 
  User, 
  Edit, 
  Trash2,
  Calendar,
  Stethoscope,
  Syringe,
  Activity,
  X,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getPets, addPet, updatePet, deletePet } from '../services/petService';
import { getOwners } from '../services/ownerService';
import { getAppointments } from '../services/appointmentService';
import { getExaminations } from '../services/examinationService';
import { getVaccinations } from '../services/vaccinationService';
import { Pet, Owner, Appointment, Examination, Vaccination } from '../types';
import PetForm from '../components/Forms/PetForm';
import DeleteConfirmModal from '../components/Modals/DeleteConfirmModal';
import { SkeletonListPage } from '../components/Skeleton/Skeleton';
import EmptyState from '../components/EmptyState/EmptyState';
import PetAvatar from '../components/Avatar/PetAvatar';
import { Cake, Weight as WeightIcon, Palette as PaletteIcon } from 'lucide-react';
import { getVaccinationStatus, getDueDate } from '../utils/vaccinationStatus';
import { normalizeToLocalDate, todayLocal } from '../utils/dateUtils';
import { useQueryClient } from '@tanstack/react-query';
import { normalizeTr } from '../utils/normalizeTr';

// Helper function to format dates, moved here to be accessible by both components
const formatDate = (dateString: string) => {
  if (!dateString) return 'Tarih belirtilmemiş';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    // Geçersiz tarih geldiğinde uygulamayı kırmamak için güvenli dönüş
    return 'Geçersiz tarih';
  }
  return new Intl.DateTimeFormat('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date);
};

// Prop types for the detail modal
interface PetDetailModalProps {
  pet: Pet & { owner?: Owner };
  examinations: Examination[];
  vaccinations: Vaccination[];
  onClose: () => void;
}

const displayAge = (ageInMonths: number) => {
  if (!ageInMonths || ageInMonths <= 0) return 'Belirtilmemiş';
  if (ageInMonths > 10000) return 'Belirtilmemiş'; // bozuk veri koruması
  if (ageInMonths < 12) return `${ageInMonths} aylık`;
  const years = Math.floor(ageInMonths / 12);
  const months = ageInMonths % 12;
  return months === 0 ? `${years} yaşında` : `${years} yaş ${months} aylık`;
};

const translateSpecies = (species: string) => {
  const map: Record<string, string> = {
    cat: 'Kedi', dog: 'Köpek', bird: 'Kuş', rabbit: 'Tavşan',
    hamster: 'Hamster', fish: 'Balık', turtle: 'Kaplumbağa', other: 'Diğer'
  };
  return map[species?.toLowerCase()] || species;
};

const PetDetailModal: React.FC<PetDetailModalProps> = ({ pet, examinations, vaccinations, onClose }) => {
  const [activeTab, setActiveTab] = useState('info');

  // Geçmiş: uygulanmış dozlar. Gelecek: sonraki doz tarihi henüz karşılanmamış (yaklaşan/planlı/gecikmiş) kayıtlar.
  const pastVaccinations = vaccinations.filter(v => normalizeToLocalDate(v.date) <= todayLocal());
  const futureVaccinations = vaccinations
    .filter(v => getVaccinationStatus(v, vaccinations) !== 'completed')
    .sort((a, b) => (getDueDate(a) || a.date).localeCompare(getDueDate(b) || b.date));

  const renderContent = () => {
    switch (activeTab) {
      case 'examinations':
        return (
          <div className="space-y-4">
            {examinations.length > 0 ? examinations.map(ex => (
              <div key={ex.id} className="p-3 bg-[#F7F4ED] rounded-lg">
                <p className="font-semibold text-[#2B2B2B]">{formatDate(ex.date)}</p>
                <p className="text-sm text-[#8A8676]"><strong>Teşhis:</strong> {ex.diagnosis}</p>
                <p className="text-sm text-[#8A8676]"><strong>Tedavi:</strong> {ex.treatment}</p>
              </div>
            )) : <p className="text-[#8A8676]">Muayene kaydı bulunamadı.</p>}
          </div>
        );
      case 'vaccinations':
        return (
          <div>
            <h4 className="font-semibold mb-2 text-[#2B2B2B]">Gelecek Aşılar</h4>
            <div className="space-y-3 mb-4">
              {futureVaccinations.length > 0 ? futureVaccinations.map(vac => (
                <div key={vac.id} className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>{vac.vaccine_name}</strong> - {formatDate(getDueDate(vac) || vac.date)}
                    {getVaccinationStatus(vac, vaccinations) === 'overdue' && <span className="ml-2 text-red-600 font-semibold">(gecikmiş)</span>}
                  </p>
                </div>
              )) : <p className="text-[#8A8676]">Yaklaşan aşı bulunmuyor.</p>}
            </div>
            <h4 className="font-semibold mb-2 text-[#2B2B2B]">Geçmiş Aşılar</h4>
            <div className="space-y-3">
              {pastVaccinations.length > 0 ? pastVaccinations.map(vac => (
                <div key={vac.id} className="p-3 bg-[#F7F4ED] rounded-lg">
                  <p className="text-sm text-[#8A8676]"><strong>{vac.vaccine_name}</strong> - {formatDate(vac.date)}</p>
                </div>
              )) : <p className="text-[#8A8676]">Geçmiş aşı kaydı bulunamadı.</p>}
            </div>
          </div>
        );
      case 'info':
      default:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[#2B2B2B]">
            <p><strong>Sahip:</strong> {pet.owner?.name || 'Belirtilmemiş'}</p>
            <p><strong>Tür:</strong> {translateSpecies(pet.species)}</p>
            <p><strong>Irk:</strong> {pet.breed || 'Belirtilmemiş'}</p>
            <p><strong>Yaş:</strong> {displayAge(pet.age)}</p>
            <p><strong>Cinsiyet:</strong> {pet.gender === 'male' ? 'Erkek' : pet.gender === 'female' ? 'Dişi' : 'Belirtilmemiş'}</p>
            <p><strong>Kilo:</strong> {pet.weight ? `${pet.weight} kg` : 'Belirtilmemiş'}</p>
            {pet.protocolNo && <p><strong>Protokol No:</strong> {pet.protocolNo}</p>}
          </div>
        );
    }
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center z-[9999] p-4" style={{backgroundColor:"rgba(0,0,0,0.75)"}}
    >
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="dribbble-panel max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-[#E8E2D4]">
          <div className="flex items-center space-x-3">
            <PawPrint className="w-6 h-6 text-[#557236]" />
            <div>
              <h2 className="text-lg font-bold text-[#2B2B2B]">{pet.name}</h2>
              {pet.protocolNo && <p className="text-xs text-[#8A8676]">Protokol: {pet.protocolNo}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[#B0A99A] hover:text-[#8A8676] rounded-full hover:bg-[#F0EBE1]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 border-b border-[#E8E2D4]">
          <div className="flex space-x-1 bg-[#F0EBE1] p-1 rounded-lg">
            <button onClick={() => setActiveTab('info')} className={`w-full py-2 text-sm rounded-md transition-colors ${activeTab === 'info' ? 'bg-white shadow text-[#2B2B2B]' : 'text-[#8A8676] hover:bg-[#F0EBE1]'}`}>Genel Bilgiler</button>
            <button onClick={() => setActiveTab('examinations')} className={`w-full py-2 text-sm rounded-md transition-colors ${activeTab === 'examinations' ? 'bg-white shadow text-[#2B2B2B]' : 'text-[#8A8676] hover:bg-[#F0EBE1]'}`}>Muayeneler</button>
            <button onClick={() => setActiveTab('vaccinations')} className={`w-full py-2 text-sm rounded-md transition-colors ${activeTab === 'vaccinations' ? 'bg-white shadow text-[#2B2B2B]' : 'text-[#8A8676] hover:bg-[#F0EBE1]'}`}>Aşılar</button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto">
          {renderContent()}
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

const Pets: React.FC = () => {
  const queryClient = useQueryClient();
  const [pets, setPets] = useState<Pet[]>([]);
  const [owners, setOwners] = useState<Owner[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [examinations, setExaminations] = useState<Examination[]>([]);
  const [vaccinations, setVaccinations] = useState<Vaccination[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'name' | 'breed' | 'ownerName' | 'age'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPet, setSelectedPet] = useState<Pet & { owner?: Owner } | null>(null);
  
  // PetForm için state'ler
  const [showPetForm, setShowPetForm] = useState(false);
  const [editingPet, setEditingPet] = useState<Pet | null>(null);
  // Silme onayı
  const [petToDelete, setPetToDelete] = useState<Pet | null>(null);

  const location = useLocation();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [petsData, ownersData, appointmentsData, examinationsData, vaccinationsData] = await Promise.all([
        getPets(),
        getOwners(),
        getAppointments(),
        getExaminations(),
        getVaccinations()
      ]);
      
      setPets(petsData);
      setOwners(ownersData);
      setAppointments(appointmentsData);
      setExaminations(examinationsData);
      setVaccinations(vaccinationsData);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      setError('Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Veriler yüklendikten ve URL'de bir pet ID'si varsa modalı aç
    if (pets.length > 0 && owners.length > 0) {
      const params = new URLSearchParams(location.search);
      const petId = params.get('pet');
      if (petId) {
        const petToShow = pets.find(p => p.id === petId);
        if (petToShow) {
          const petWithOwner = getPetWithOwner(petToShow);
          handleShowDetail(petWithOwner);
        }
      }
    }
  }, [location.search, pets, owners]); // URL veya veriler değiştiğinde çalıştır

  const getPetWithOwner = (pet: Pet) => {
    const owner = owners.find(o => o.id === pet.owner_id);
    return { ...pet, owner };
  };

  const handleSort = (field: 'name' | 'breed' | 'ownerName' | 'age') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: 'name' | 'breed' | 'ownerName' | 'age') => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  // PetForm işlemleri
  const handleAddPet = () => {
    setEditingPet(null);
    setShowPetForm(true);
  };

  const handleEditPet = (pet: Pet) => {
    setEditingPet(pet);
    setShowPetForm(true);
  };

  const handlePetSubmit = async (petData: Omit<Pet, 'id' | 'createdAt'>) => {
    try {
      if (editingPet) {
        // Hasta güncelle
        const updatedPet = await updatePet(editingPet.id, petData);
        setPets(prev => prev.map(p => p.id === editingPet.id ? { ...p, ...updatedPet } : p));
        toast.success('Hasta başarıyla güncellendi!');
      } else {
        // Yeni hasta ekle
        const newPet = await addPet(petData);
        setPets(prev => [...prev, newPet]);
        toast.success('Hasta başarıyla eklendi!');
      }
      setShowPetForm(false);
      setEditingPet(null);
    } catch (error: any) {
      toast.error('Hata: ' + error.message);
    }
  };

  const handleDeletePet = (petId: string) => {
    const pet = pets.find(p => p.id === petId);
    if (pet) setPetToDelete(pet);
  };

  const confirmDeletePet = async () => {
    if (!petToDelete) return;
    try {
      await deletePet(petToDelete.id);
      setPets(prev => prev.filter(pet => pet.id !== petToDelete.id));
      ['pets', 'vaccinations', 'appointments', 'examinations'].forEach(k =>
        queryClient.invalidateQueries({ queryKey: [k] })
      );
      toast.success('Hasta kaydı silindi!');
    } catch (error: any) {
      toast.error('Silinemedi: ' + error.message);
    }
  };

  const filteredPets = pets
    .map(getPetWithOwner)
    .filter(pet => {
      const matchesSearch = 
        normalizeTr(pet.name).includes(normalizeTr(searchTerm)) ||
        normalizeTr(pet.breed).includes(normalizeTr(searchTerm)) ||
        normalizeTr(pet.owner?.name).includes(normalizeTr(searchTerm));
      return matchesSearch;
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'breed':
          aValue = a.breed || '';
          bValue = b.breed || '';
          break;
        case 'ownerName':
          aValue = a.owner?.name || '';
          bValue = b.owner?.name || '';
          break;
        case 'age':
          aValue = a.age || 0;
          bValue = b.age || 0;
          break;
        default:
          return 0;
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleShowDetail = (pet: Pet & { owner?: Owner }) => {
    setSelectedPet(pet);
    setShowDetailModal(true);
  };

  if (loading) return <SkeletonListPage />;
  if (error) return <div className="text-center py-12 text-red-600">{error}</div>;

  return (
    <div className="page-stack">
      {/* Page Header */}
      <div className="page-head">
        <div>
          <h1 className="page-title">Hastalar</h1>
          <p className="page-subtitle">Tüm hasta kayıtları</p>
        </div>
        <button
          className="btn btn-primary self-start sm:self-auto"
          onClick={handleAddPet}
        >
          <Plus className="w-4 h-4" />
          <span>Yeni Hasta</span>
        </button>
      </div>

      {/* Search */}
      <div className="dribbble-panel p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#B0A99A] w-5 h-5" />
          <input
            type="text"
            placeholder="Hasta ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {/* Empty state — tablo başlıkları olmadan */}
      {filteredPets.length === 0 ? (
        <div className="dribbble-panel overflow-hidden">
          <EmptyState
            icon={PawPrint}
            iconColor="amber"
            title={pets.length === 0 ? "Henüz hasta kaydı yok" : "Sonuç bulunamadı"}
            description={pets.length === 0
              ? "İlk hayvanınızı kaydederek hasta takibini başlatın."
              : "Arama kriterlerinize uygun hasta bulunamadı. Filtreleri temizleyip tekrar deneyin."}
            actionLabel={pets.length === 0 ? "Yeni Hasta Ekle" : undefined}
            onAction={pets.length === 0 ? () => setShowPetForm(true) : undefined}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredPets.map((pet, index) => {
            return (
              <motion.div
                key={pet.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => handleShowDetail(pet)}
                className="dribbble-panel p-4 cursor-pointer group relative"
              >
                {/* Üst — Dinamik renkli avatar + isim */}
                <div className="flex items-start gap-3 mb-3">
                  <PetAvatar name={pet.name} species={pet.species} size={48} />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#2B2B2B] truncate">{pet.name}</p>
                    <p className="text-xs text-[#8A8676] truncate">{translateSpecies(pet.species)} · {pet.breed || '—'}</p>
                  </div>
                </div>

                {/* Bilgi rozetleri — emoji yerine ikon */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {pet.age != null && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F0EBE1] text-[#2B2B2B]">
                      <Cake className="w-3 h-3" />
                      {displayAge(pet.age)}
                    </span>
                  )}
                  {pet.weight && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F0EBE1] text-[#2B2B2B]">
                      <WeightIcon className="w-3 h-3" />
                      {pet.weight} kg
                    </span>
                  )}
                  {pet.gender && pet.gender !== 'unknown' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F0EBE1] text-[#2B2B2B]">
                      {pet.gender === 'male' ? '♂ Erkek' : '♀ Dişi'}
                    </span>
                  )}
                  {pet.color && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F0EBE1] text-[#2B2B2B]">
                      <PaletteIcon className="w-3 h-3" />
                      {pet.color}
                    </span>
                  )}
                </div>

                {/* Sahip */}
                <div className="flex items-center gap-1.5 text-xs text-[#8A8676] pt-3 border-t border-[#F0EBE1]">
                  <User className="w-3 h-3 text-[#557236]" />
                  <span className="font-medium truncate">{pet.owner?.name || 'Sahipsiz'}</span>
                </div>

                {/* Aksiyon — hover ile görünür */}
                <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleEditPet(pet); }}
                    className="w-7 h-7 rounded-lg bg-white shadow-md border border-[#E8E2D4] hover:bg-[#E8EFD9] text-[#557236] flex items-center justify-center"
                    title="Düzenle"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeletePet(pet.id); }}
                    className="w-7 h-7 rounded-lg bg-white shadow-md border border-[#E8E2D4] hover:bg-red-50 text-red-600 flex items-center justify-center"
                    title="Sil"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* PetForm Modal */}
      <PetForm
        pet={editingPet}
        isOpen={showPetForm}
        onClose={() => {
          setShowPetForm(false);
          setEditingPet(null);
        }}
        onSubmit={handlePetSubmit}
        owners={owners}
      />

      {/* Silme Onay Modal */}
      <DeleteConfirmModal
        isOpen={!!petToDelete}
        onClose={() => setPetToDelete(null)}
        onConfirm={confirmDeletePet}
        title="Hastayı Sil"
        message={(() => {
          if (!petToDelete) return 'Bu hayvanı silmek istediğinizden emin misiniz?';
          const isThis = (x: any) => (x.pet_id || x.petId) === petToDelete.id;
          const a = appointments.filter(isThis).length;
          const e = examinations.filter(isThis).length;
          const v = vaccinations.filter(isThis).length;
          return a + e + v > 0
            ? `Bu hastaya bağlı ${e} muayene, ${v} aşı ve ${a} randevu kaydı var. Hasta silindiğinde bu kayıtlar da silinebilir. Bu işlem geri alınamaz.`
            : 'Bu hayvanı silmek istediğinizden emin misiniz?';
        })()}
        itemName={petToDelete?.name}
      />

      {/* Enhanced Pet Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedPet && (
          <PetDetailModal 
            pet={selectedPet}
            examinations={examinations.filter(e => e.pet_id === selectedPet!.id)}
            vaccinations={vaccinations.filter(v => v.pet_id === selectedPet!.id)}
            onClose={() => setShowDetailModal(false)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Pets;